import { EvaluationScoreLabel, EvaluationStatus } from './evaluation/calculate.types';
import { LeaveDurationTypeEnum, LeaveRequestStatus } from './leaveRequest.types';
import { Page } from './pageable.types';

export enum RoleTypeEnum {
    OWNER = 'OWNER',
    ADMIN = 'ADMIN',
    PM = 'PM',
    TEAM_LEAD = 'TEAM_LEAD',
    ORDINARY = 'ORDINARY',
    BASIC = 'BASIC',
    HR = 'HR',
}

export type UserProfileDto = {
    userSub: string;
    imageUrl?: string;
    givenName: string;
    familyName: string;
    phoneNumber?: string;
    country?: string;
    title?: string;
    dateOfBirth?: string;
    religiousAffiliation?: string;
    privateEmail?: string;
    salary?: string;
    salaryFrequency?: string;
    bankName?: string;
    bankAccountNumber?: string;
    dietaryRestrictions?: string;
    children: { name: string; dateOfBirth: string }[];
    emergencyContact?: EmergencyContact;
    updatedAt?: string;
};

export type UserProfileWithRoleDto = UserProfileDto & {
    role: RoleDto;
};

type PatchProfileDto = {
    imageUrl?: string;
    givenName?: string;
    familyName?: string;
    phoneNumber?: string;
    title?: string;
    country?: string;
    salary?: string;
    salaryFrequency?: string;
    bankName?: string;
    bankAccountNumber?: string;
    religiousAffiliation?: string;
    privateEmail?: string;
    childrenCount?: number;
    dietaryRestrictions?: string;
    children?: {
        name: string;
        dateOfBirth: string;
    }[];
    emergencyContact?: EmergencyContact;
};

export type RoleDto = {
    name: RoleTypeEnum;
};

type WidgetConfig = {
    label: string;
    name: string;
    visible: boolean;
};

export type PolicyDto = {
    id: string;
    userSub: string;
    leaveType: string;
    organizationPolicyId: string;
    amount: number;
    initialAmount: number;
    initialCarriedOverAmount: number;
    carriedOverAmount: number;
    isCarriedOver: boolean;
    maxCarriedOverAmount: number;
    carriedOverValidToMonth: number;
    carriedOverValidToDay: number;
    shortestLeaveDuration: LeaveDurationTypeEnum;
    createdAt: Date;
    updatedAt: Date;
};

type ConfigurationDto = {
    userSub: string;
    widgets?: WidgetConfig[];
};

export type TokensDto = {
    idToken: string;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
};

export type UserDto = {
    userSub: string;
    email: string;
    organizationId: string;
    profile: UserProfileDto;
    role: RoleDto;
    configuration: ConfigurationDto;
    branchId?: string | null;
    joinedAt?: Date | null;
};

export type PatchUserDto = {
    email?: string;
    profile?: PatchProfileDto;
    role?: RoleDto;
    configuration?: ConfigurationDto;
    joinedAt?: string;
    contractEndDate?: string;
};

export type PublicUserDto = {
    userSub: string;
    email: string;
    policies: PolicyDto[];
    organizationId: string;
    givenName: string;
    familyName: string;
    role: RoleDto;
    imageUrl?: string;
    branchId?: string | null;
    title?: string;
};

export type PaginatedPublicUserDto = Page<PublicUserDto>;

export type PaginatedUserDocumentsDto = Page<UserDocumentDto>;

export type PublicUserWithoutPoliciesDto = {
    userSub: string;
    email: string;
    organizationId?: string | null;
    givenName: string;
    familyName: string;
    role: RoleDto;
    imageUrl?: string;
};

export type PublicUserWithoutPoliciesAndRoleDto = {
    userSub: string;
    email: string;
    givenName: string;
    familyName: string;
    imageUrl?: string;
};

export type PublicUserDisplayProfileEnrichment = {
    userSub: string;
    givenName?: string;
    familyName?: string;
    email?: string;
    imageUrl?: string;
    title?: string;
    displayName?: string;
    profile?: {
        givenName?: string;
        familyName?: string;
        imageUrl?: string;
        title?: string;
    };
};

export type ChangeUserRoleDto = {
    userSub: string;
    newRole: string;
};

export type OrganizationMembersCount = {
    totalMembers: number;
    joinedAtCount: number;
};

export type PublicUserWithBirthday = {
    userSub: string;
    email: string;
    givenName: string;
    familyName: string;
    imageUrl?: string;
    dateOfBirth?: string;
};

export type PublicUserWithAnniversary = {
    userSub: string;
    email: string;
    givenName: string;
    familyName: string;
    imageUrl?: string;
    joinedAt?: string | null;
};

export type GoogleIntegration = {
    isCalendarEnabled: boolean;
    isGmailAutoReplyEnabled: boolean;
    gmailAutoReplySubject: string;
    gmailAutoReplyText: string;
};

export type PatchGoogleIntegration = {
    gmailAutoReplySubject?: string;
    gmailAutoReplyText?: string;
};

type EmergencyContact = {
    name?: string;
    relation?: string;
    phoneNumber?: string;
    email?: string;
};

export type UserDocumentDto = {
    id: string;
    s3name: string;
    name: string;
    userSub: string;
    organizationId: string;
    createdAt: string;
    updatedAt: string;
    description?: string;
    categoryName?: string;
    categoryId?: string;
};

export type UserDocumentPostDto = {
    name: string;
    userSub: string;
    file: File;
    categoryId: string;
    onUploaded: (userDocument: UserDocumentDto) => void;
    description?: string;
};
export type CategoryDto = {
    name: string;
    description?: string;
    notificationOn?: boolean;
};

export type PatchCategoryDto = {
    name?: string;
    description?: string;
    notificationOn?: boolean;
};

export type PatchDocumentDto = {
    categoryId?: string;
    name?: string;
    description?: string;
};
export type Category = {
    id: string;
    organizationId: string;
    name: string;
    description?: string;
    notificationOn?: boolean;
    markedAsObsolete?: boolean;
    createdAt: string;
    updatedAt: string;
};

export type UserDocumentPresignedDto = UserDocumentDto & {
    presignedUrl: string;
};

export type RecalculatePoliciesAmountForUserDTO = {
    userSub: string;
    joinedAt: string;
    contractEndDate?: string | null;
};

export enum EvaluationSortBy {
    EVALUATOR = 'evaluator',
    EVALUATEE = 'evaluatee',
    ROLE = 'role',
    DUE_DATE = 'dueDate',
    STATUS = 'status',
    EXPIRATION_STATUS = 'expirationStatus',
    UPDATE_REASON = 'updateReason',
    IS_ANONYMOUS = 'isAnonymous',
}

export type EvaluationFilterDto = {
    status?: EvaluationStatus;
    sortBy?: EvaluationSortBy;
    sortDirection?: 'ASC' | 'DESC';
    page?: number;
    pageSize?: number;
};
export type RequestEvaluationDto = {
    evaluatorUserSub: string;
    evaluateeUserSubs: string[];
    evaluatorToEvaluateeRoleId: string;
    dueDate: string;
    isAnonymous: boolean;
    isNoteRequired: boolean;
    topicIds: string[];
};

export type TopicDto = {
    id: string;
    name: string;
    topicCategoryId: string;
    description: string;
    positiveScoreDescription: string;
    neutralScoreDescription: string;
    negativeScoreDescription: string;
    setName?: string;
};

export interface PerformEvaluationDto {
    topicScores: {
        topicId: string;
        score: string | null;
        note?: string | null;
    }[];
}

export type EvaluationRoles = {
    id: string;
    direction: string;
};

export type EvaluationRoleTopicWeight = {
    topicId: string;
    weight: number;
};
export type MedicalLeaveRequestBaseResponseDto = {
    id: string;
    userSub: string;
    organizationId: string;
    description?: string | null;
    requesterUserSub: string;
    startDate: string;
    endDate?: string | null;
    status: LeaveRequestStatus;
    createdAt: string;
    updatedAt: string;
    familyName: string;
    givenName: string;
    imageUrl: string;
    policyType?: string;
    rejectReason?: string;
    medicalLeaveRequestDocument?: MedicalLeaveRequestDocument[];
};

export type MedicalLeaveRequestDocument = {
    id: string;
    name: string;
    s3name: string;
};

export type MedicalLeaveRequestWithUserProfileDto = {
    id: string;
    userSub: string;
    organizationId: string;
    description?: string | null;
    requesterUserSub: string;
    startDate: string;
    endDate?: string | null;
    status: LeaveRequestStatus;
    createdAt: string;
    updatedAt: string;
    user?: {
        profile?: {
            givenName: string;
            familyName: string;
            imageUrl: string;
        };
    };
    medicalLeaveRequestDocument?: MedicalLeaveRequestDocument[];
};

export type MedicalLeaveRequestPostDto = {
    startDate: string;
    endDate?: string;
    files: File[];
    description?: string;
    status: LeaveRequestStatus;
    userSub: string;
};

export type TopicScoreDto = {
    score: EvaluationScoreLabel | null;
    note?: string | null;
};
